import {
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { format } from "date-fns";
import { FreightRateGroup } from "../freight-rate-group/freight-rate-group.model";
import { SelectItem } from "primeng/api";
import { FreightRate } from "../models/freight-rate.model";
import { Bind } from "primeng/bind";
import { MultiSelect } from "primeng/multiselect";
import { FormsModule } from "@angular/forms";

@Component({
  selector: "app-freight-rate-batch-select",
  templateUrl: "./freight-rate-batch-select.component.html",
  styleUrls: ["./freight-rate-batch-select.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Bind, MultiSelect, FormsModule],
})
export class FreightRateBatchSelectComponent implements OnInit, OnChanges {
  public readonly freightRateGroups = input<Record<string, FreightRateGroup>>(
    {},
  );
  public readonly locationKindFilterOptions = input<string[]>([]);
  public readonly saving = input(false);
  public readonly selectionChange = output<{
    selected: FreightRate[];
    deselected: FreightRate[];
  }>();

  public contractSelectList: SelectItem[] = [];
  public selectedContracts: FreightRate[] = [];
  public placeholder = "Please select contracts";

  constructor() {}

  ngOnInit() {
    this.buildContractDropdownList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.freightRateGroups) {
      this.buildContractDropdownList();
      this.getSelectedContracts();
    } else if (changes.saving) {
      // The panel stays open while saving, so its options grey out too.
      this.contractSelectList = this.contractSelectList.map((item) => ({
        ...item,
        disabled: this.saving(),
      }));
    }
  }

  // One event per change, so the whole selection is saved in one request. The
  // dropdown keeps showing what is saved until the parent re-ticks it through
  // getSelectedContracts() once the request settles.
  public emitFreightRates(requested: FreightRate[]) {
    const selected: FreightRate[] = [];
    const deselected: FreightRate[] = [];
    Object.values(this.freightRateGroups()).forEach((group) => {
      group.freightRates.forEach((freightRate) => {
        if (this.isFreightRateInSelection(freightRate, requested)) {
          selected.push(freightRate);
        } else {
          deselected.push(freightRate);
        }
      });
    });
    // A new array, so ngModel writes the saved selection back over the click.
    this.selectedContracts = [...this.selectedContracts];
    this.selectionChange.emit({ selected, deselected });
  }

  private isFreightRateInSelection(
    freightRate: FreightRate,
    contracts: FreightRate[],
  ): boolean {
    let isFreightRatePresent = false;

    contracts.forEach((selectedContract) => {
      if (
        selectedContract.getRate().getId() === freightRate.getRate().getId()
      ) {
        isFreightRatePresent = true;
      }
    });

    return isFreightRatePresent;
  }

  public getSelectedContracts() {
    this.selectedContracts = [];

    this.contractSelectList.forEach((selectItem) => {
      if (selectItem.value.getRoute()) {
        this.selectedContracts.push(selectItem.value);
      }
    });
  }

  private buildContractDropdownList() {
    this.contractSelectList = [];

    Object.keys(this.freightRateGroups()).forEach((groupKey) => {
      const group = this.freightRateGroups()[groupKey];
      group.freightRates.forEach((freightRate) => {
        if (!this.isFreightRateOnContractDropdown(freightRate)) {
          this.contractSelectList.push({
            label: this.getContractLabel(freightRate),
            value: freightRate,
            disabled: this.saving(),
          });
        }
      });
    });
  }

  private getContractLabel(freightRate: FreightRate): string {
    if (
      !!freightRate &&
      !!freightRate.getRate() &&
      !!freightRate.getRate().getName() &&
      !!freightRate.getRate().getCarrier() &&
      !!freightRate.getRate().getValidity()
    ) {
      return (
        freightRate.getRate().getName() +
        " - " +
        freightRate.getRate().getCarrier().getName() +
        " - " +
        format(freightRate.getRate().getValidity()[0], "dd.MM") +
        " - " +
        format(freightRate.getRate().getValidity()[1], "dd.MM")
      );
    }

    return this.placeholder;
  }

  private isFreightRateOnContractDropdown(
    freightRateToCheck: FreightRate,
  ): boolean {
    let isIdOnList = false;
    this.contractSelectList.forEach((selectItem) => {
      if (
        selectItem.value.getRate().getId() ===
        freightRateToCheck.getRate().getId()
      ) {
        isIdOnList = true;
      }
    });

    return isIdOnList;
  }
}
